/**
 * Les liens de pointage de la garderie : la fenêtre de l'écran « Présences
 * du centre ».
 *
 * Un lien par école, valable l'année scolaire, à ouvrir sur un téléphone, sans
 * compte. Ici : choisir l'école, garder ou changer le code à quatre chiffres,
 * créer le lien, le recopier pour WhatsApp, voir s'il sert, le révoquer. La
 * base fait le reste (migration `20261031200000_le_pointage_de_la_garderie`).
 */
import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, Check, Copy, Link2, Loader2, RefreshCw, Trash2, X } from 'lucide-react'
import { adresseDuLien, messagePourLesAnims } from '../../../lib/garderie/pointage'
import { etatDesCodesFaux } from '../../../lib/liens-codes'
import {
  creerLeLien,
  lireLesEcoles,
  lireLesLiens,
  revoquerLeLien,
  type EcoleDeGarderie,
  type LienDeGarderie,
} from '../../../lib/garderie/pointage.supabase'

const codeAleatoire = (): string => {
  const n = new Uint32Array(1)
  crypto.getRandomValues(n)
  return String(n[0] % 10000).padStart(4, '0')
}

const ilYA = (iso: string | null): string => {
  if (!iso) return 'jamais'
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (min < 1) return 'à l’instant'
  if (min < 60) return `il y a ${min} min`
  const h = Math.round(min / 60)
  if (h < 24) return `il y a ${h} h`
  return `il y a ${Math.round(h / 24)} j`
}

export function LiensGarderie({ onClose }: { onClose: () => void }) {
  const [ecoles, setEcoles] = useState<EcoleDeGarderie[]>([])
  const [ecoleId, setEcoleId] = useState<string>('')
  const [code, setCode] = useState<string>(() => codeAleatoire())
  const [liens, setLiens] = useState<LienDeGarderie[]>([])
  const [chargement, setChargement] = useState(true)
  const [creation, setCreation] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [copie, setCopie] = useState<string | null>(null)
  const [aRevoquer, setARevoquer] = useState<LienDeGarderie | null>(null)

  const origine = typeof window !== 'undefined' ? window.location.origin : ''

  const charger = useCallback(async () => {
    setChargement(true)
    setErreur(null)
    try {
      const [e, l] = await Promise.all([lireLesEcoles(), lireLesLiens()])
      setEcoles(e)
      setLiens(l)
      setEcoleId((id) => id || e[0]?.centerId || '')
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Impossible de lire les liens')
    } finally {
      setChargement(false)
    }
  }, [])

  useEffect(() => {
    void charger()
  }, [charger])

  const creer = async () => {
    if (!ecoleId) return
    setCreation(true)
    setErreur(null)
    try {
      if (code.length !== 4) {
        setErreur('Le code fait quatre chiffres : la fiche des enfants est derrière le lien.')
        return
      }
      await creerLeLien(ecoleId, code)
      await charger()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'La création a échoué')
    } finally {
      setCreation(false)
    }
  }

  const revoquer = async (lien: LienDeGarderie) => {
    setARevoquer(null)
    setErreur(null)
    try {
      await revoquerLeLien(lien.id)
      await charger()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'La révocation a échoué')
    }
  }

  const copier = async (lien: LienDeGarderie) => {
    const texte = messagePourLesAnims({ ecole: lien.ecole, adresse: adresseDuLien(origine, lien.jeton), code: lien.code })
    try {
      await navigator.clipboard.writeText(texte)
      setCopie(lien.id)
      setTimeout(() => setCopie((c) => (c === lien.id ? null : c)), 2000)
    } catch {
      setErreur('Le presse-papiers est indisponible : copiez l’adresse à la main.')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} role="presentation">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Liens de pointage de la garderie</h2>
            <p className="text-sm text-gray-600">Un lien par école, valable toute l’année scolaire. Les animateurs l’ouvrent sur leur téléphone et cochent les présents.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-gray-100" aria-label="Fermer">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
          <label className="text-sm">
            <span className="block font-medium text-gray-700">École</span>
            <select value={ecoleId} onChange={(e) => setEcoleId(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2">
              {ecoles.length === 0 && <option value="">Aucune école avec un catalogue de garderie</option>}
              {ecoles.map((e) => (
                <option key={e.centerId} value={e.centerId}>
                  {e.nom} — {e.anneeScolaire}-{e.anneeScolaire + 1}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="block font-medium text-gray-700">Code</span>
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
              className="mt-1 w-24 rounded-lg border border-gray-300 px-3 py-2 text-center tracking-widest"
            />
          </label>
          <button
            type="button"
            onClick={() => void creer()}
            disabled={creation || !ecoleId}
            className="flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 font-semibold text-white disabled:opacity-40"
          >
            {creation ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            Créer le lien
          </button>
        </div>
        <p className="mt-1 text-xs text-gray-500">Créer un lien pour une école qui en a déjà un remplace l’ancien : les anciens téléphones devront rouvrir le nouveau.</p>

        {erreur && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{erreur}</span>
          </div>
        )}

        <div className="mt-5 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Liens en service</h3>
          <button type="button" onClick={() => void charger()} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900" disabled={chargement}>
            <RefreshCw className={`h-4 w-4 ${chargement ? 'animate-spin' : ''}`} /> Relire
          </button>
        </div>
        {chargement && liens.length === 0 ? (
          <Loader2 className="mx-auto mt-6 h-6 w-6 animate-spin text-primary-500" />
        ) : liens.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">Aucun lien pour l’instant.</p>
        ) : (
          <ul className="mt-3 divide-y divide-gray-100 rounded-xl border border-gray-200">
            {liens.map((l) => (
              <li key={l.id} className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">{l.ecole}</p>
                  <p className="truncate text-xs text-gray-500">{adresseDuLien(origine, l.jeton)}</p>
                  <p className="text-xs text-gray-500">
                    Code {l.code ?? '—'} · {l.pointages} pointage(s) · dernier usage {ilYA(l.dernierUsageLe)} · expire le {new Date(l.expireLe).toLocaleDateString('fr-BE')}
                    {etatDesCodesFaux(l.essaisCode) ? ` · ${etatDesCodesFaux(l.essaisCode)}` : ''}
                  </p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <button type="button" onClick={() => void copier(l)} className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50">
                    {copie === l.id ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />} Copier le message
                  </button>
                  {aRevoquer?.id === l.id ? (
                    <button type="button" onClick={() => void revoquer(l)} className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white">
                      Confirmer
                    </button>
                  ) : (
                    <button type="button" onClick={() => setARevoquer(l)} className="rounded-lg p-2 text-red-600 hover:bg-red-50" aria-label="Révoquer">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
